import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { HashLink } from './heading.js';
import classNames from 'classnames';
import { Tooltip } from './components/index.js';
import { MyST } from './MyST.js';
const BASIC_RENDERERS = {
    text({ node }) {
        var _a;
        // Change zero-width space into `<wbr>` which is better for copying
        // These are used in links, and potentially other long words
        if (!((_a = node.value) === null || _a === void 0 ? void 0 : _a.includes('​'))) {
            return _jsx(_Fragment, { children: node.value });
        }
        const text = node.value.split('​');
        return (_jsx(_Fragment, { children: text.map((v, i) => (_jsxs(React.Fragment, { children: [v, i < text.length - 1 && _jsx("wbr", {})] }, i))) }));
    },
    span({ node }) {
        return (_jsx("span", { className: node.class, style: node.style, children: _jsx(MyST, { ast: node.children }) }));
    },
    div({ node }) {
        return (_jsx("div", { className: node.class, style: node.style, children: _jsx(MyST, { ast: node.children }) }));
    },
    delete({ node }) {
        return (_jsx("del", { children: _jsx(MyST, { ast: node.children }) }));
    },
    strong({ node }) {
        return (_jsx("strong", { children: _jsx(MyST, { ast: node.children }) }));
    },
    emphasis({ node }) {
        return (_jsx("em", { children: _jsx(MyST, { ast: node.children }) }));
    },
    underline({ node }) {
        return (_jsx("span", { style: { textDecoration: 'underline' }, children: _jsx(MyST, { ast: node.children }) }));
    },
    smallcaps({ node }) {
        return (_jsx("span", { style: { fontVariant: 'small-caps' }, children: _jsx(MyST, { ast: node.children }) }));
    },
    link({ node }) {
        return (_jsx("a", { target: "_blank", href: node.url, rel: "noreferrer", children: _jsx(MyST, { ast: node.children }) }));
    },
    paragraph({ node }) {
        return (_jsx("p", { id: node.html_id, children: _jsx(MyST, { ast: node.children }) }));
    },
    algorithmLine({ node }) {
        var _a;
        // Used in algorithms
        const style = {
            paddingLeft: `${((_a = node.indent) !== null && _a !== void 0 ? _a : 0) + 2}rem`,
        };
        return (_jsx("p", { className: "line", style: style, "data-line-number": node.enumerator, children: _jsx(MyST, { ast: node.children }) }));
    },
    break() {
        return _jsx("br", {});
    },
    inlineMath({ node }) {
        return _jsx("code", { children: node.value });
    },
    math({ node }) {
        return _jsx("code", { children: node.value });
    },
    list({ node }) {
        if (node.ordered) {
            return (_jsx("ol", { start: node.start || undefined, id: node.html_id, children: _jsx(MyST, { ast: node.children }) }));
        }
        return (_jsx("ul", { id: node.html_id, children: _jsx(MyST, { ast: node.children }) }));
    },
    listItem({ node }) {
        if (node.checked == null) {
            return (_jsx("li", { children: _jsx(MyST, { ast: node.children }) }));
        }
        return (_jsxs("li", { className: "task-list-item", children: [_jsx("input", { type: "checkbox", className: "task-list-item-checkbox", defaultChecked: node.checked }), _jsx(MyST, { ast: node.children })] }));
    },
    container({ node }) {
        return (_jsx("figure", { id: node.html_id || node.identifier || node.key, className: classNames({ subcontainer: node.subcontainer }, node.class), children: _jsx(MyST, { ast: node.children }) }));
    },
    caption({ node }) {
        return (_jsx("figcaption", { className: "group", children: _jsx(MyST, { ast: node.children }) }));
    },
    legend({ node }) {
        return (_jsx("figcaption", { className: "text-sm", children: _jsx(MyST, { ast: node.children }) }));
    },
    blockquote({ node }) {
        return (_jsx("blockquote", { id: node.html_id, children: _jsx(MyST, { ast: node.children }) }));
    },
    thematicBreak() {
        return _jsx("hr", { className: "py-2 my-5 translate-y-2" });
    },
    captionNumber({ node }) {
        const id = node.html_id || node.identifier || node.key;
        return (_jsx(HashLink, { id: id, kind: node.kind, className: "mr-1 font-semibold text-inherit hover:text-inherit hover:font-semibold", children: _jsx(MyST, { ast: node.children }) }));
    },
    table({ node }) {
        // TODO: actually render the tbody on the server if it isn't included here.
        return (_jsx("table", { className: node.class, style: node.style, children: _jsx("tbody", { children: _jsx(MyST, { ast: node.children }) }) }));
    },
    tableRow({ node }) {
        return (_jsx("tr", { className: node.class, style: node.style, children: _jsx(MyST, { ast: node.children }) }));
    },
    tableCell({ node }) {
        const ifGreaterThanOne = (num) => (num === 1 ? undefined : num);
        const attrs = {
            rowSpan: ifGreaterThanOne(node.rowspan),
            colSpan: ifGreaterThanOne(node.colspan),
        };
        if (node.header)
            return (_jsx("th", Object.assign({ className: node.class, style: node.style }, attrs, { children: _jsx(MyST, { ast: node.children }) })));
        return (_jsx("td", Object.assign({ className: node.class, style: node.style }, attrs, { children: _jsx(MyST, { ast: node.children }) })));
    },
    subscript({ node }) {
        return (_jsx("sub", { children: _jsx(MyST, { ast: node.children }) }));
    },
    superscript({ node }) {
        return (_jsx("sup", { children: _jsx(MyST, { ast: node.children }) }));
    },
    abbreviation({ node }) {
        return (_jsx(Tooltip, { title: node.title, children: _jsx("abbr", { "aria-label": node.title, className: "border-b border-dotted cursor-help", children: _jsx(MyST, { ast: node.children }) }) }));
    },
    mystComment() {
        return null;
    },
    comment() {
        return null;
    },
    definitionList({ node }) {
        return (_jsx("dl", { className: "my-5", id: node.html_id, children: _jsx(MyST, { ast: node.children }) }));
    },
    definitionTerm({ node }) {
        var _a, _b;
        const allowedStrongTypes = new Set(['text', 'emphasis']);
        const makeStrong = (_b = (_a = node.children) === null || _a === void 0 ? void 0 : _a.reduce((allowed, n) => allowed && allowedStrongTypes.has(n.type), true)) !== null && _b !== void 0 ? _b : false;
        return (_jsx("dt", { id: node.html_id, children: makeStrong ? (_jsx("strong", { children: _jsx(MyST, { ast: node.children }) })) : (_jsx(MyST, { ast: node.children })) }));
    },
    definitionDescription({ node }) {
        return (_jsx("dd", { children: _jsx(MyST, { ast: node.children }) }));
    },
};
export default BASIC_RENDERERS;
